import React from 'react'
import get from 'lodash/get'
import className from 'classnames'
import smoothscroll from 'smoothscroll-polyfill'
import Head from 'next/head'
import {useRouter} from 'next/router'
import dynamic from 'next/dynamic'

import {t} from '@lib/helpers'
import '../app.scss'
import {useGTMPageview, UTMContextProvider} from '@lib/gtm'
import {CustomAiFooter} from '@components/custom-ai'

const Nav = dynamic(() => import('@components/Nav'))
const GetStarted = dynamic(() => import('@components/GetStarted'))
const Footer = dynamic(() => import('@components/Footer'))
const FooterWithForm = dynamic(() => import('@components/FooterWithForm'))
const Meta = dynamic(() => import('@components/Meta'))
const SiteHeader = dynamic(() => import('@components/SiteHeader'))

const MyApp = (props) => {
  const router = useRouter()
  const [isActive, isActiveSet] = React.useState(false)

  useGTMPageview()

  React.useEffect(() => {
    smoothscroll.polyfill()
  }, [])

  React.useEffect(() => {
    // Not to activate transition right away to prevent
    // first-load premature transition.
    isActiveSet(true)
  }, [])

  const withFormPath = ['/services/data-labeling']
  const withCustomAiFormPath = ['/custom-ai']

  return (
    <React.Fragment>
      {(() => {
        const _host =
          typeof window != 'undefined'
            ? get(window, 'location.origin')
            : 'https://datawow.io'

        const url = `${_host}${get(router, 'asPath')}`

        return (
          <React.Fragment>
            <Meta
              title={t('layout.title')}
              description={t('layout.description')}
              image='banner.png'
            />

            <Head>
              <meta charSet='utf-8' />
              <meta
                name='viewport'
                content='width=device-width, initial-scale=1, maximum-scale=1'
              />
              <meta name='robots' content='index,follow' />
              <meta name='googlebot' content='index,follow' />

              <meta name='og:url' property='og:url' content={url} />
              <meta name='og:type' property='og:type' content='website' />

              <meta name='twitter:card' content='summary_large_image' />
              <meta name='twitter:site' content={url} />
              {/* <meta name="twitter:creator" content="@" /> */}

              <link rel='preconnect' href='https://www.google-analytics.com' />
              <link rel='preconnect' href='https://stats.g.doubleclick.net' />

              <link rel='canonical' href={get(url.split('?'), '0')} />

              <link
                rel='apple-touch-icon'
                sizes='180x180'
                href='/apple-touch-icon.png'
              />
              <link
                rel='icon'
                type='image/png'
                sizes='32x32'
                href='/favicon-32x32.png'
              />
              <link
                rel='icon'
                type='image/png'
                sizes='16x16'
                href='/favicon-16x16.png'
              />
              <link rel='manifest' href='/site.webmanifest' />
              <link
                rel='mask-icon'
                href='/safari-pinned-tab.svg'
                color='#5bbad5'
              />
              <meta name='msapplication-TileColor' content='#ffffff' />
              <meta name='theme-color' content='#ffffff' />
              <script
                data-type='gtm'
                dangerouslySetInnerHTML={{
                  __html: `
                  (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                  'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                  })(window,document,'script','dataLayer','GTM-MHG3C7V');
                    `,
                }}
              />
            </Head>
          </React.Fragment>
        )
      })()}
      <UTMContextProvider>
        {withFormPath.includes(get(router, 'pathname')) ? (
          <div
            className={className('Layout', {
              'Layout--active': isActive,
            })}
          >
            <SiteHeader />

            <props.Component
              {...{
                ...props.pageProps,
                ...props.pageProps,
              }}
            />

            <FooterWithForm />
          </div>
        ) : withCustomAiFormPath.includes(get(router, 'pathname')) ? (
          <div
            className={className('Layout', {
              'Layout--active': isActive,
            })}
          >
            <SiteHeader />

            <props.Component
              {...{
                ...props.pageProps,
                ...props.pageProps,
              }}
            />

            <CustomAiFooter />
          </div>
        ) : (
          <div
            className={className('Layout', {
              'Layout--active': isActive,
            })}
          >
            <SiteHeader />

            <props.Component
              {...{
                ...props.pageProps,
                ...props.pageProps,
              }}
            />

            <GetStarted />

            <Footer />
          </div>
        )}
      </UTMContextProvider>
    </React.Fragment>
  )
}

export default MyApp
